import React from 'react';
import round from 'lodash/round';
import GeneralChart from '../GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';
import { numberRound } from '@/utils/numbers';

const Summary = () => {
  const {
    avg,
    avg_median: avgMedian,
    name,
    latest_month_avg_salary: latestMonthAvgSalary,
    adjacent_cities: adjacentCities,
  } = React.useContext(CityContext);
  const lineChartRef = React.useRef(null);
  const rank = adjacentCities.find((item) => item.current_city)?.rank || 0;

  const lineConfig = React.useMemo<{
    config: ECOption;
    seoInfo: string;
  }>(() => {
    const allKeys = Object.keys(latestMonthAvgSalary).sort();
    const latestKeys = allKeys.slice(-3);
    const parseIntMonth = latestKeys.map((item) =>
      parseInt(item.split('-')[1]),
    );
    const avgSalary = latestKeys.map((key) =>
      round(latestMonthAvgSalary[key], -2),
    );
    const lastYearAvgSalary = latestKeys.map((key) => {
      const [year, month] = key.split('-');
      const lastYearKey = `${Number(year) - 1}-${month}`;
      const val = latestMonthAvgSalary[lastYearKey];
      return val ? round(val, -2) : null;
    });

    const allSalaryValues = [
      ...avgSalary,
      ...lastYearAvgSalary.filter((v): v is number => v !== null),
    ];
    const rawMin = Math.min(...allSalaryValues);
    const rawMax = Math.max(...allSalaryValues);
    const yAxisMin = Math.max(0, Math.floor(rawMin / 1000) * 1000 - 1000);
    const yAxisMax = Math.ceil(rawMax / 1000) * 1000 + 1000;

    const description = `${name}平均工资${avg >= (avgMedian || 0) ? '高' : '低'}于全国中位水平（${avgMedian ? round(Math.abs((avg - avgMedian) / avgMedian), 1) : '--'}%），在所有城市中排名第${rank}`;

    return {
      config: {
        grid: {
          show: false,
          top: '20%',
          bottom: '10%',
          left: '10%',
          right: '8%',
        },
        xAxis: {
          type: 'category',
          data: parseIntMonth,
          axisTick: { show: false },
          axisLine: { show: false },
          boundaryGap: false,
          axisLabel: {
            fontSize: 12,
            formatter: (value) => value + '月',
            color: '#6e6e6e',
          },
        },
        yAxis: {
          type: 'value',
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: {
            fontSize: 12,
            color: '#6e6e6e',
            formatter: (value: number) => numberRound(value).replace('k', 'K'),
          },
          splitNumber: 2,
          min: yAxisMin,
          max: yAxisMax,
          splitLine: {
            show: true,
            lineStyle: {
              type: 'solid',
              color: '#f0f0f0',
              width: 1,
            },
          },
        },
        legend: {
          show: true,
          type: 'plain',
          right: 0,
          icon: 'circle',
          itemHeight: 6,
          itemWidth: 6,
          itemGap: 8,
          selectedMode: false,
          textStyle: {
            padding: [0, 0, 0, 2],
            fontSize: 12,
            color: '#2d2d2d',
            backgroundColor: 'transparent',
          },
        },
        series: [
          {
            name: '今年',
            type: 'line',
            zlevel: 1,
            data: avgSalary,
            smooth: true,
            cursor: 'default',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#0050b3',
            },
            lineStyle: {
              width: 2,
            },
            emphasis: {
              scale: 1.4,
              itemStyle: {
                color: '#0050b3',
              },
              label: {
                show: true,
                fontSize: 12,
                color: '#2d2d2d',
                backgroundColor: 'transparent',
                formatter: ({ value }) => (value ? '¥' + value : ''),
              },
            },
          },
          {
            name: '去年同期',
            type: 'line',
            zlevel: 1,
            data: lastYearAvgSalary,
            smooth: true,
            cursor: 'default',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#dbe2fd',
            },
            lineStyle: {
              width: 2,
            },
            emphasis: {
              scale: 1.4,
              itemStyle: {
                color: '#dbe2fd',
              },
              label: {
                show: true,
                fontSize: 12,
                color: '#2d2d2d',
                backgroundColor: 'transparent',
                formatter: ({ value }) => (value ? '¥' + value : ''),
              },
            },
          },
        ],
      },
      seoInfo: description,
    };
  }, [latestMonthAvgSalary]);

  return (
    <GeneralChart padding="p-0" margin="m-0">
      <div className="flex">
        <div className="p-8 pb-3 max-w-[365px] border-r border-white-smoke flex flex-col">
          <h2 className="font-semibold mb-2">平均月薪</h2>
          <p className="text-4.5xl font-semibold mb-4">{`¥${round(avg, -2)}`}</p>
          <p className="text-sm mb-2">{lineConfig.seoInfo}</p>
          <span className="mt-auto text-xs text-web-grey-600">
            本数据统计来源于各大平台发布的公开数据，仅供参考。
          </span>
        </div>
        <div className="p-8 grow">
          <h3 className="font-semibold mb-4">近三月数据同期对比</h3>
          <div className="h-50 [&_canvas]:cursor-default">
            <CusEcharts chartRef={lineChartRef} config={lineConfig.config} />
          </div>
        </div>
      </div>
    </GeneralChart>
  );
};

export default Summary;
